// src/services/VersionCheckService.js - Modified for silent updates

/**
 * Service to handle application version checking and silent updates
 */
const VersionCheckService = {
  // Current app version - update this when you make significant changes
  APP_VERSION: '1.1.0',
  
  // Storage key for last version
  STORAGE_KEY: 'app_version',

  // Cache name for static resources - must match the service worker's cache name
  STATIC_CACHE_NAME: 'trinity-harper-static-v1.1',
  
  /**
   * Check if app needs updating
   * @returns {Object} Result with update status
   */
  checkForUpdates: () => {
    try {
      // Get the stored version (if any)
      const storedVersion = localStorage.getItem(VersionCheckService.STORAGE_KEY);
      
      // If no stored version or versions don't match, update is needed
      if (!storedVersion || storedVersion !== VersionCheckService.APP_VERSION) {
        console.log(`Silent update needed from ${storedVersion || 'unknown'} to ${VersionCheckService.APP_VERSION}`);
        
        // Perform silent update immediately
        VersionCheckService.performUpdate();
        
        return {
          updateNeeded: true,
          updatePerformed: true,
          currentVersion: VersionCheckService.APP_VERSION,
          previousVersion: storedVersion || 'unknown'
        };
      }
      
      return {
        updateNeeded: false,
        currentVersion: VersionCheckService.APP_VERSION
      };
    } catch (error) {
      console.error('Error checking for app updates:', error);
      return {
        updateNeeded: false,
        error: error.message
      };
    }
  },
  
  /**
   * Perform a silent update by updating the stored version and refreshing caches
   * @returns {Promise<boolean>} Success status
   */
  performUpdate: async () => {
    try {
      console.log('Performing silent app update...');
      
      // Store the new version
      localStorage.setItem(VersionCheckService.STORAGE_KEY, VersionCheckService.APP_VERSION);
      
      // Only clear the STATIC_CACHE_NAME, not document caches
      if ('caches' in window) {
        try {
          const cache = await caches.open(VersionCheckService.STATIC_CACHE_NAME);
          const requests = await cache.keys();
          await Promise.all(
            requests.map(request => {
              // Only delete app code resources, not document resources
              if (!request.url.includes('firebasestorage')) {
                return cache.delete(request);
              }
              return Promise.resolve();
            })
          );
          console.log('App code cache cleared for update');
        } catch (cacheError) {
          console.error('Error clearing app code cache:', cacheError);
        }
      }
      
      // Don't clear document-related IndexedDB data
      
      return true;
    } catch (error) {
      console.error('Error performing update:', error);
      return false;
    }
  },
  
  /**
   * Clear all app data for troubleshooting (preserved for admin/developer use)
   */
  clearAllData: () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear caches
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }
      
      console.log('All app data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing app data:', error);
      return false;
    }
  }
};

export default VersionCheckService;
